import { replaceClickThrough, setupClickThroughOverride } from './clickthrough-override';
import { detectAbnormalAspectRatio } from './creative-validators';
import { ControlsContextValueType } from './creative/contexts/controlsContext';
import { InjectBuilderCSS } from './inject-css';
import { replaceClickthruMacros, setupClickthruMacros } from './macros';
import { changes, emit, subscribe, unsubscribe } from './subs';
import { initTracking } from './tracking';
import { AdParameters, Attrs, ClickThruOptions, Indefinite, TimeUpdate, ViewMode } from './types';

export interface AdRendererParams {
  slot: HTMLElement;
  controls: ControlsContextValueType;
  videoSlot: HTMLVideoElement;
  adParameters: AdParameters<any>;
  width: number;
  height: number;
}

export interface AdRenderer {
  (params: AdRendererParams): void;
}

let JSXAdRenderer: AdRenderer = () => null;

export function setJSXAdRenderer(fn: AdRenderer) {
  JSXAdRenderer = fn;
}

const attrs: Attrs = {
  linear: true,
  width: 0,
  height: 0,
  expanded: false,
  skippable: false,
  remainingTime: Indefinite,
  duration: Indefinite,
  viewMode: 'normal',
  bitrate: 256,
  volume: 1,
  companions: '',
  icons: false,
  quartile: 0,
};

let adVideoComplete = false;

function checkAdComplete({ remainingTime }: TimeUpdate) {
  if (!adVideoComplete && remainingTime == 0) {
    adVideoComplete = true;
    applyChange('AdVideoComplete');
  }
}

let nextQuartile = 0;
const quartiles = [
  { event: 'AdVideoFirstQuartile', value: 0.25 },
  { event: 'AdVideoMidpoint', value: 0.5 },
  { event: 'AdVideoThirdQuartile', value: 0.75 },
];

function sendQuartiles({ remainingTime, duration }: TimeUpdate) {
  const quartile = (duration - remainingTime) / duration;
  if (nextQuartile < quartiles.length && quartile >= quartiles[nextQuartile].value) {
    applyChange(quartiles[nextQuartile].event);
    nextQuartile = nextQuartile + 1;
  }
}

function updateDuration(timeUpdate: TimeUpdate) {
  if (adVideoComplete) return;
  applyChange('AdDurationChange', timeUpdate);
}

function updateTime(timeUpdate: TimeUpdate) {
  updateDuration(timeUpdate);
  sendQuartiles(timeUpdate);
  checkAdComplete(timeUpdate);
}

function clickThru({ url, id, playerHandles }: ClickThruOptions) {
  emit('AdClickThru', replaceClickthruMacros(replaceClickThrough(url)), id, playerHandles);
}

function stopAd() {
  return VPAIDAd.stopAd();
}

function setAdLoaded() {
  emit('AdLoaded');
}

function applyChange(event: string, partial: Partial<Attrs> = {}) {
  Object.assign(attrs, partial);
  switch (event) {
    case 'AdStarted':
      changes.applyChange({ event: 'start', partial });
      changes.applyChange({ event: 'play', partial });
      emit('AdVideoStart');
      emit('AdImpression');
      emit(event);
      break;
    case 'AdPlaying':
      if (adVideoComplete) break;
      changes.applyChange({ event: 'play', partial });
      emit(event);
      break;
    case 'AdPaused':
      if (adVideoComplete) break;
      changes.applyChange({ event: 'pause', partial });
      emit(event);
      break;
    case 'AdSkipped':
    case 'AdVideoComplete':
      changes.applyChange({ event: 'stop', partial });
      emit(event);
      break;
    case 'AdVolumeChange':
      changes.applyChange({ event: 'volume', partial });
      emit(event);
      break;
    default:
      changes.applyChange({ event, partial });
      emit(event);
  }
}

let adParameters: AdParameters<unknown>;

const VPAIDAd = {
  initAd: (
    width: number,
    height: number,
    viewMode: ViewMode,
    bitrate: number,
    creativeData: { AdParameters: string },
    environmentVars: any
  ) => {
    try {
      setupClickthruMacros();
      setupClickThroughOverride();
      Object.assign(attrs, { width, height, bitrate, viewMode });
      const { slot, videoSlot } = environmentVars;
      adParameters = JSON.parse(creativeData['AdParameters']);
      const controls: ControlsContextValueType = {
        renderSlot: slot,
        clickThru,
        updateTime,
        stopAd,
        setAdLoaded,
        changes,
      };
      InjectBuilderCSS(slot);
      initTracking();
      JSXAdRenderer({ slot, controls, videoSlot, adParameters, width, height });
      adVideoComplete = false;
    } catch (e) {
      console.error(e);
      throw e;
    }
  },
  resizeAd: (width: number, height: number, viewMode: ViewMode) => {
    applyChange('AdSizeChange', { width, height, viewMode });

    detectAbnormalAspectRatio(adParameters.creativeId, width, height);
  },
  startAd: () => applyChange('AdStarted'),
  stopAd: () => applyChange('AdStopped'),
  pauseAd: () => applyChange('AdPaused'),
  resumeAd: () => applyChange('AdPlaying'),
  expandAd: () => applyChange('AdExpandedChange', { expanded: true }),
  collapseAd: () => applyChange('AdExpandedChange', { expanded: false }),
  skipAd: () => applyChange('AdSkipped'),
  setAdVolume: (volume: number) => applyChange('AdVolumeChange', { volume }),
  getAdLinear: () => attrs.linear,
  getAdWidth: () => attrs.width,
  getAdHeight: () => attrs.height,
  getAdExpanded: () => attrs.expanded,
  getAdSkippableState: () => attrs.skippable,
  getAdRemainingTime: () => attrs.remainingTime,
  getAdDuration: () => attrs.duration,
  getAdCompanions: () => attrs.companions,
  getAdIcons: () => attrs.icons,
  getAdVolume: () => attrs.volume,
  handshakeVersion: () => '2.0',
  subscribe,
  unsubscribe,
};
(window as any).getVPAIDAd = () => VPAIDAd;
